import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../services/api';

interface SupportTicket {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  assigned_to: string | null;
  estimated_resolution: string | null;
  comments_count: number;
}

interface TicketDetails extends SupportTicket {
  attachments: any[];
  comments: any[];
  resolved_at: string | null;
  closed_at: string | null;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  helpful_count: number;
  not_helpful_count: number;
  related_articles: string[];
  related_faqs: string[];
}

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  category: string;
  author: string;
  cover_image: string | null;
  published_at: string;
  updated_at: string;
  views: number;
  rating: number;
  rating_count?: number;
  read_time: number;
  tags?: string[];
  related_articles?: any[];
}

interface SupportState {
  tickets: SupportTicket[];
  selectedTicket: TicketDetails | null;
  faqs: FAQ[];
  articles: Article[];
  selectedArticle: Article | null;
  categories: {
    faq_categories: string[];
    article_categories: string[];
  };
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  limit: number;
}

const initialState: SupportState = {
  tickets: [],
  selectedTicket: null,
  faqs: [],
  articles: [],
  selectedArticle: null,
  categories: {
    faq_categories: [],
    article_categories: []
  },
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  limit: 10
};

export const createTicket = createAsyncThunk(
  'support/createTicket',
  async (ticketData: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/support/tickets', ticketData);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create ticket');
    }
  }
);

export const fetchTickets = createAsyncThunk(
  'support/fetchTickets',
  async ({ status, priority, page, limit }: { status?: string; priority?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const params: any = { page: page || 1, limit: limit || 10 };
      if (status) params.status = status;
      if (priority) params.priority = priority;

      const response = await apiClient.get('/support/tickets', { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tickets');
    }
  }
);

export const fetchTicket = createAsyncThunk(
  'support/fetchTicket',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/support/tickets/${ticketId}`);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch ticket');
    }
  }
);

export const addTicketComment = createAsyncThunk(
  'support/addTicketComment',
  async ({ ticketId, message, attachments }: { ticketId: string; message: string; attachments?: any[] }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/support/tickets/${ticketId}/comments`, {
        message,
        attachments
      });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add comment');
    }
  }
);

export const fetchFAQs = createAsyncThunk(
  'support/fetchFAQs',
  async ({ category, search, page, limit }: { category?: string; search?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const params: any = { page: page || 1, limit: limit || 10 };
      if (category) params.category = category;
      if (search) params.search = search;

      const response = await apiClient.get('/support/faqs', { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch FAQs');
    }
  }
);

export const markFAQHelpful = createAsyncThunk(
  'support/markFAQHelpful',
  async ({ faqId, helpful }: { faqId: string; helpful: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/support/faqs/${faqId}/helpful`, { helpful });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark FAQ');
    }
  }
);

export const fetchArticles = createAsyncThunk(
  'support/fetchArticles',
  async ({ category, search, page, limit }: { category?: string; search?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const params: any = { page: page || 1, limit: limit || 10 };
      if (category) params.category = category;
      if (search) params.search = search;

      const response = await apiClient.get('/support/articles', { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch articles');
    }
  }
);

export const fetchArticle = createAsyncThunk(
  'support/fetchArticle',
  async (slug: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/support/articles/${slug}`);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch article');
    }
  }
);

export const rateArticle = createAsyncThunk(
  'support/rateArticle',
  async ({ articleId, rating }: { articleId: string; rating: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/support/articles/${articleId}/rate`, { rating });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to rate article');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'support/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/support/categories');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
    clearSelectedArticle: (state) => {
      state.selectedArticle = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTicket.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickets = action.payload.tickets;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTicket.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicket.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedTicket = action.payload;
      })
      .addCase(fetchTicket.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addTicketComment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTicketComment.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addTicketComment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFAQs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFAQs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.faqs = action.payload.faqs;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchFAQs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(markFAQHelpful.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markFAQHelpful.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(markFAQHelpful.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.articles = action.payload.articles;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedArticle = action.payload;
      })
      .addCase(fetchArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(rateArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rateArticle.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(rateArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      });
  }
});

export const { clearError, setPage, clearSelectedTicket, clearSelectedArticle } = supportSlice.actions;
export default supportSlice.reducer;
